import type {
  SceneModel,
  SlimePaletteId,
  SlimeSpec,
} from "../types";
import {
  MAX_PUBLIC_PET_NAME_LENGTH,
  MAX_PUBLIC_ROOM_HISTORY_SOURCE_SCENES,
  MAX_PUBLIC_ROOM_PETS,
  PRIVATE_PROJECTION_SEED_PATTERN,
  PUBLIC_PET_HAT_VALUES,
  PUBLIC_ROOM_SNAPSHOT_SCHEMA_VERSION,
  PUBLIC_ROOM_TICKER_PATTERN,
} from "./constants";
import {
  neutralAppearanceForPublicPetV1,
  publicPetIdForSourceV1,
} from "./privacy";
import {
  DEFAULT_SHARE_VISIBILITY_POLICY_V1,
  type PublicAllocationBucketV1,
  type PublicPerformanceDirectionV1,
  type PublicPerformanceSignalV1,
  type PublicPetAppearanceV1,
  type PublicPetPaletteV1,
  type PublicRoomHistoryPetV1,
  type PublicRoomHistoryPointV1,
  type PublicRoomPetV1,
  type PublicRoomSnapshotV1,
  type ShareHistoryVisibilityV1,
  type ShareVisibilityPolicyV1,
} from "./schema";
import {
  validatePublicRoomSnapshotV1,
  validateShareVisibilityPolicyV1,
  type PublicRoomValidationIssue,
  type PublicRoomValidationResult,
} from "./validation";

/**
 * Public snapshot v1 must keep its original eight-value palette contract.
 * The hidden-identity derivation hashes modulo that fixed list, so widening
 * it in-place would invalidate existing share links. Local-only presets are
 * projected to the closest authored v1 color until a dual-read v2 exists.
 */
const PUBLIC_PALETTE_V1_BY_LOCAL_ID: Readonly<
  Record<SlimePaletteId, PublicPetPaletteV1>
> = {
  leaf: "leaf",
  lime: "leaf",
  lemon: "honey",
  sky: "sky",
  aqua: "sky",
  cobalt: "sky",
  denim: "sky",
  coral: "coral",
  cherry: "coral",
  rose: "berry",
  honey: "honey",
  mango: "honey",
  tangerine: "apricot",
  violet: "violet",
  indigo: "violet",
  grape: "violet",
  orchid: "berry",
  mint: "mint",
  teal: "mint",
  lagoon: "sky",
  berry: "berry",
  apricot: "apricot",
  forest: "leaf",
  moss: "leaf",
  cocoa: "apricot",
};

export interface BuildPublicRoomSnapshotV1Input {
  readonly scene: SceneModel;
  /**
   * Must be freshly generated for the share session. It deliberately replaces
   * SceneModel.sceneSeed at the public boundary.
   */
  readonly publicSeed: string;
  /**
   * Private, random per-share pseudonym key. It belongs in SecretStore beside
   * the manage credential and must never be persisted in ShareSessionRecord or
   * copied into PublicRoomSnapshotV1. Keeping it separate prevents a viewer
   * from hashing likely ticker/holding identifiers with the public layout seed.
   */
  readonly privacySeed: string;
  readonly visibility?: ShareVisibilityPolicyV1;
  /**
   * Optional canonical observations. The builder includes the current scene,
   * bounds the requested calendar range, and never copies source DTO fields.
   */
  readonly historyScenes?: readonly SceneModel[];
}

function projectionFailure<T = PublicRoomSnapshotV1>(
  path: readonly (string | number)[],
  message: string,
): PublicRoomValidationResult<T> {
  const fieldPath = path.reduce<string>(
    (result, segment) =>
      typeof segment === "number"
        ? `${result}[${segment}]`
        : result
          ? `${result}.${segment}`
          : segment,
    "",
  );
  const issue: PublicRoomValidationIssue = {
    code: "invalid_value",
    path,
    fieldPath,
    message,
  };
  return { success: false, issues: [issue] };
}

function compareStrings(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function normalizedName(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  let result = "";
  for (const character of trimmed) {
    if (result.length + character.length > MAX_PUBLIC_PET_NAME_LENGTH) {
      break;
    }
    result += character;
  }
  return result || undefined;
}

function publicAppearance(
  slime: SlimeSpec,
  publicSeed: string,
  publicId: string,
  identityHidden: boolean,
): PublicPetAppearanceV1 {
  if (identityHidden) {
    return neutralAppearanceForPublicPetV1(publicSeed, publicId);
  }
  const publicHatId = PUBLIC_PET_HAT_VALUES.find(
    (candidate) => candidate === slime.appearance.hatId,
  );
  return {
    paletteId: PUBLIC_PALETTE_V1_BY_LOCAL_ID[slime.appearance.paletteId],
    patternId: slime.appearance.patternId,
    ...(publicHatId ? { hatId: publicHatId } : {}),
  };
}

function directionForValue(
  value: number | null | undefined,
): PublicPerformanceDirectionV1 {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "unknown";
  }
  return value > 0 ? "up" : value < 0 ? "down" : "flat";
}

function performanceForValue(
  value: number | null | undefined,
  policy: ShareVisibilityPolicyV1["performance"],
): PublicPerformanceSignalV1 | undefined {
  if (policy === "hidden") {
    return undefined;
  }
  if (policy === "direction") {
    return { direction: directionForValue(value) };
  }
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return { direction: "unknown" };
  }
  const roundedPercent = Math.max(-100, Math.min(100, Math.round(value)));
  return {
    direction: directionForValue(roundedPercent),
    roundedPercent: Object.is(roundedPercent, -0) ? 0 : roundedPercent,
  };
}

function allocationForWeight(
  weight: number,
): PublicAllocationBucketV1 | undefined {
  if (!Number.isFinite(weight) || weight < 0) {
    return undefined;
  }
  if (weight < 0.05) return "tiny";
  if (weight < 0.15) return "small";
  if (weight < 0.35) return "medium";
  return "large";
}

function sourceIdentity(slime: SlimeSpec): string {
  return slime.instrumentId || slime.id;
}

function orderedSlimes(
  scene: SceneModel,
  privacySeed: string,
  policy: ShareVisibilityPolicyV1,
): readonly SlimeSpec[] {
  return [...scene.slimes].sort((left, right) => {
    if (policy.identity === "ticker") {
      const leftIdentity = [
        left.ticker,
        left.displayName,
        sourceIdentity(left),
      ].join("\u0000");
      const rightIdentity = [
        right.ticker,
        right.displayName,
        sourceIdentity(right),
      ].join("\u0000");
      const identityDelta = compareStrings(
        leftIdentity,
        rightIdentity,
      );
      if (identityDelta !== 0) return identityDelta;
    }
    return compareStrings(
      publicPetIdForSourceV1(
        privacySeed,
        sourceIdentity(left),
      ),
      publicPetIdForSourceV1(
        privacySeed,
        sourceIdentity(right),
      ),
    );
  });
}

function projectPet(
  slime: SlimeSpec,
  publicSeed: string,
  privacySeed: string,
  policy: ShareVisibilityPolicyV1,
): PublicRoomPetV1 {
  const id = publicPetIdForSourceV1(
    privacySeed,
    sourceIdentity(slime),
  );
  const performance = performanceForValue(
    slime.dailyReturnPct,
    policy.performance,
  );
  const allocationBucket =
    policy.allocation === "bucket"
      ? allocationForWeight(slime.weight)
      : undefined;
  const pet: {
    id: string;
    appearance: PublicPetAppearanceV1;
    ticker?: string;
    name?: string;
    logoKey?: string;
    performance?: PublicPerformanceSignalV1;
    allocationBucket?: PublicAllocationBucketV1;
  } = {
    id,
    appearance: publicAppearance(
      slime,
      publicSeed,
      id,
      policy.identity === "hidden",
    ),
  };

  if (policy.identity === "ticker") {
    const ticker = slime.ticker.trim().toUpperCase();
    if (PUBLIC_ROOM_TICKER_PATTERN.test(ticker)) {
      pet.ticker = ticker;
      // Logo resolution remains local/versioned; no third-party URL is sent.
      pet.logoKey = ticker;
    }
    const name = normalizedName(slime.displayName || slime.name);
    if (name) pet.name = name;
  }
  if (performance) pet.performance = performance;
  if (allocationBucket) pet.allocationBucket = allocationBucket;
  return pet;
}

function historyRangeDays(
  policy: Exclude<ShareHistoryVisibilityV1, "none">,
): number {
  switch (policy) {
    case "7d":
      return 7;
    case "30d":
      return 30;
    case "90d":
      return 90;
  }
}

/** Proleptic Gregorian calendar → an ordinal suitable for date differences. */
function calendarDayNumber(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const leap =
    year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [
    31,
    leap ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ][month - 1];
  if (
    year < 1 ||
    year > 9_999 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > (daysInMonth ?? 0)
  ) {
    return null;
  }
  const adjustedYear = year - (month <= 2 ? 1 : 0);
  const era = Math.floor(adjustedYear / 400);
  const yearOfEra = adjustedYear - era * 400;
  const adjustedMonth = month + (month > 2 ? -3 : 9);
  const dayOfYear =
    Math.floor((153 * adjustedMonth + 2) / 5) + day - 1;
  const dayOfEra =
    yearOfEra * 365 +
    Math.floor(yearOfEra / 4) -
    Math.floor(yearOfEra / 100) +
    dayOfYear;
  return era * 146_097 + dayOfEra;
}

function historyPets(
  scene: SceneModel,
  currentIdsBySource: ReadonlyMap<string, string>,
  policy: ShareVisibilityPolicyV1,
): readonly PublicRoomHistoryPetV1[] {
  if (
    policy.performance === "hidden" &&
    policy.allocation === "hidden"
  ) {
    return [];
  }
  return scene.slimes
    .flatMap((slime): PublicRoomHistoryPetV1[] => {
      const id = currentIdsBySource.get(sourceIdentity(slime));
      if (!id) return [];
      const performance = performanceForValue(
        slime.dailyReturnPct,
        policy.performance,
      );
      const allocationBucket =
        policy.allocation === "bucket"
          ? allocationForWeight(slime.weight)
          : undefined;
      return [
        {
          id,
          ...(performance ? { performance } : {}),
          ...(allocationBucket ? { allocationBucket } : {}),
        },
      ];
    })
    .sort((left, right) => left.id.localeCompare(right.id));
}

function buildHistory(
  current: SceneModel,
  historyScenes: readonly SceneModel[],
  currentIdsBySource: ReadonlyMap<string, string>,
  policy: ShareVisibilityPolicyV1,
): PublicRoomValidationResult<PublicRoomHistoryPointV1[]> {
  if (policy.history === "none") {
    return { success: true, data: [] };
  }
  const currentDay = calendarDayNumber(current.marketDate);
  if (currentDay === null) {
    return projectionFailure(
      ["scene", "marketDate"],
      "과거 공개에는 유효한 현재 시장 날짜가 필요합니다.",
    ) as PublicRoomValidationResult<PublicRoomHistoryPointV1[]>;
  }
  const firstDay = currentDay - (historyRangeDays(policy.history) - 1);
  const byDate = new Map<string, SceneModel>();
  for (const scene of [...historyScenes, current]) {
    const day = calendarDayNumber(scene.marketDate);
    if (day !== null && day >= firstDay && day <= currentDay) {
      byDate.set(scene.marketDate, scene);
    }
  }
  const points = [...byDate.values()]
    .sort((left, right) => left.marketDate.localeCompare(right.marketDate))
    .map(
      (scene): PublicRoomHistoryPointV1 => ({
        marketDate: scene.marketDate,
        room: { weather: scene.room.weather },
        pets: historyPets(scene, currentIdsBySource, policy),
      }),
    );
  return { success: true, data: points };
}

/**
 * Projects the already-derived local scene through an allowlist. The function
 * never spreads source objects, so new private fields added to SceneModel do
 * not become public by accident.
 */
export function buildPublicRoomSnapshotV1(
  input: BuildPublicRoomSnapshotV1Input,
): PublicRoomValidationResult<PublicRoomSnapshotV1> {
  if (!PRIVATE_PROJECTION_SEED_PATTERN.test(input.privacySeed)) {
    return projectionFailure(
      ["privacySeed"],
      "privacySeed는 Web Crypto 32바이트를 base64url로 인코딩한 43자 비밀값이어야 합니다.",
    );
  }
  const policyResult = validateShareVisibilityPolicyV1(
    input.visibility ?? DEFAULT_SHARE_VISIBILITY_POLICY_V1,
  );
  if (!policyResult.success) return policyResult;
  const policy = policyResult.data;
  if (input.scene.slimes.length > MAX_PUBLIC_ROOM_PETS) {
    return projectionFailure(
      ["scene", "slimes"],
      `공개 펫은 ${MAX_PUBLIC_ROOM_PETS}개 이하여야 합니다.`,
    );
  }
  const historyScenes = input.historyScenes ?? [];
  if (historyScenes.length > MAX_PUBLIC_ROOM_HISTORY_SOURCE_SCENES) {
    return projectionFailure(
      ["historyScenes"],
      `과거 원본은 ${MAX_PUBLIC_ROOM_HISTORY_SOURCE_SCENES}개 이하여야 합니다.`,
    );
  }
  for (const [index, scene] of historyScenes.entries()) {
    if (scene.portfolioId !== input.scene.portfolioId) {
      return projectionFailure(
        ["historyScenes", index, "portfolioId"],
        "다른 로컬 포트폴리오의 기록을 한 공유방에 섞을 수 없습니다.",
      );
    }
    if (scene.slimes.length > MAX_PUBLIC_ROOM_PETS) {
      return projectionFailure(
        ["historyScenes", index, "slimes"],
        `과거 공개 펫은 날짜당 ${MAX_PUBLIC_ROOM_PETS}개 이하여야 합니다.`,
      );
    }
  }

  const pets = orderedSlimes(
    input.scene,
    input.privacySeed,
    policy,
  ).map((slime) =>
    projectPet(
      slime,
      input.publicSeed,
      input.privacySeed,
      policy,
    ),
  );
  const currentIdsBySource = new Map(
    input.scene.slimes.map(
      (slime) =>
        [
          sourceIdentity(slime),
          publicPetIdForSourceV1(
            input.privacySeed,
            sourceIdentity(slime),
          ),
        ] as const,
    ),
  );
  if (currentIdsBySource.size !== input.scene.slimes.length) {
    return projectionFailure(
      ["scene", "slimes"],
      "로컬 장면의 펫 identity가 중복되어 공개 ID를 만들 수 없습니다.",
    );
  }
  const historyResult = buildHistory(
    input.scene,
    historyScenes,
    currentIdsBySource,
    policy,
  );
  if (!historyResult.success) {
    return {
      success: false,
      issues: historyResult.issues,
    };
  }
  const snapshot: PublicRoomSnapshotV1 = {
    schemaVersion: PUBLIC_ROOM_SNAPSHOT_SCHEMA_VERSION,
    rendererVersion: input.scene.engineVersion,
    publicSeed: input.publicSeed,
    visibility: policy,
    room: { weather: input.scene.room.weather },
    pets,
    ...(policy.history === "none"
      ? {}
      : {
          history: {
            range: policy.history,
            points: historyResult.data,
          },
        }),
  };
  return validatePublicRoomSnapshotV1(snapshot);
}

export const projectSceneToPublicRoomSnapshotV1 =
  buildPublicRoomSnapshotV1;
